// The site options a host page sets as window.DOCBOX_* globals before docbox.js loads, with the
// legacy names and defaults (public/docbox.js:18-75). Each reader follows the legacy read: the same
// type check, the same default, and the same time of the read.

/** Where a document opens (DOCBOX_VIEW_MODE, docbox.js:18). */
export type ViewMode = "drawer" | "overlay" | "iframe" | "tab" | "inline";

const VIEW_MODES: readonly ViewMode[] = [
  "drawer",
  "overlay",
  "iframe",
  "tab",
  "inline",
];

type Globals = Record<string, unknown>;

function globals(win: Window): Globals {
  return win as unknown as Globals;
}

/**
 * DOCBOX_VIEW_MODE, case-insensitive. Any other value is the drawer (docbox.js:4883-4884). Legacy
 * reads it once, when the script loads, so the boot script does too.
 */
export function viewMode(win: Window = window): ViewMode {
  const value = globals(win).DOCBOX_VIEW_MODE;
  const mode = typeof value === "string" ? value.toLowerCase() : "drawer";
  return VIEW_MODES.find((m) => m === mode) ?? "drawer";
}

/**
 * DOCBOX_PANEL_SELECTOR, trimmed: the host element a document opens inside (docbox.js:26). Empty
 * when unset. Legacy reads it on each open (docbox.js:32842).
 */
export function panelSelector(win: Window = window): string {
  const value = globals(win).DOCBOX_PANEL_SELECTOR;
  return typeof value === "string" ? value.trim() : "";
}

/**
 * DOCBOX_MODAL_BACKDROP === true: dim the page behind a modal that is not the drawer
 * (docbox.js:43). Legacy reads it on each open (docbox.js:16740).
 */
export function modalBackdrop(win: Window = window): boolean {
  return globals(win).DOCBOX_MODAL_BACKDROP === true;
}

/**
 * DOCBOX_HOST_PREVIEW_CONTAINER_SELECTOR, trimmed: a second panel element, tried when
 * DOCBOX_PANEL_SELECTOR finds nothing (docbox.js:27, :32859-32871). Empty when unset.
 */
export function hostPreviewContainerSelector(win: Window = window): string {
  const value = globals(win).DOCBOX_HOST_PREVIEW_CONTAINER_SELECTOR;
  return typeof value === "string" ? value.trim() : "";
}

/**
 * DOCBOX_MOBILE_PANEL_MOUNT === true: a phone or tablet viewport keeps the panel element
 * (docboxPreferPanelMountOnViewport, docbox.js:14347).
 */
export function mobilePanelMount(win: Window = window): boolean {
  return globals(win).DOCBOX_MOBILE_PANEL_MOUNT === true;
}

/**
 * DOCBOX_PARSE_ON_ACCESSIBLE_CLICK_ONLY, true unless === false. Legacy reads it once, when the
 * script loads (docbox.js:4916), so the boot script does too.
 */
export function parseOnAccessibleClickOnly(win: Window = window): boolean {
  return globals(win).DOCBOX_PARSE_ON_ACCESSIBLE_CLICK_ONLY !== false;
}

/**
 * Transcript opens first only when DOCBOX_PARSE_ON_ACCESSIBLE_CLICK_ONLY was false at load and
 * DOCBOX_DEFAULT_ACCESSIBLE_FIRST === true on open (docboxDefaultAccessibleFirst,
 * docbox.js:4919-4922). The second option alone does nothing in legacy.
 */
export function transcriptFirst(
  parseOnClickOnly: boolean,
  win: Window = window,
): boolean {
  return (
    !parseOnClickOnly && globals(win).DOCBOX_DEFAULT_ACCESSIBLE_FIRST === true
  );
}

/** How long the viewer waits for a transcript. Legacy default: 10 minutes (docbox.js:34250-34256). */
export const DEFAULT_PARSE_TIMEOUT_MS = 600 * 1000;

/**
 * DOCBOX_PARSE_TIMEOUT_MS when it is a positive number, as Number() reads it. Legacy reads it on
 * each parse. Without a window (the unit tests), the default.
 */
export function parseTimeoutMs(
  win: Window | undefined = globalThis.window,
): number {
  const value = win && globals(win).DOCBOX_PARSE_TIMEOUT_MS;
  const ms = value == null ? NaN : Number(value);
  return ms > 0 && ms <= 2_147_483_647 ? ms : DEFAULT_PARSE_TIMEOUT_MS;
}

/**
 * DOCBOX_DOWNLOAD_SERVER_PDF === false: Download Original opens the document URL instead of
 * /download-pdf (buildDownloadPdfUrl, docbox.js:1636-1648).
 */
export function downloadServerPdf(win: Window = window): boolean {
  return globals(win).DOCBOX_DOWNLOAD_SERVER_PDF !== false;
}

/** DOCBOX_INTERCEPT_IFRAME_PDFS === false: watch no embedded previews (docbox.js:36410). */
export function interceptIframePdfs(win: Window = window): boolean {
  return globals(win).DOCBOX_INTERCEPT_IFRAME_PDFS !== false;
}

/**
 * Drive folder links stay with the host: DOCBOX_DEFER_DRIVE_FOLDERS_TO_HOST === true, or
 * DOCBOX_HANDLE_DRIVE_FOLDERS === false (docboxDeferGoogleDriveFolderToHost, docbox.js:7411).
 * Legacy reads them on each click.
 */
export function deferDriveFoldersToHost(win: Window = window): boolean {
  const g = globals(win);
  return (
    g.DOCBOX_DEFER_DRIVE_FOLDERS_TO_HOST === true ||
    g.DOCBOX_HANDLE_DRIVE_FOLDERS === false
  );
}

/**
 * DOCBOX_SKIP_GOOGLE_SLIDES === true: every Slides link stays with the host
 * (docboxDeferGoogleSlidesToHost, docbox.js:7436). Legacy reads it on each click. Without a
 * window (the unit tests), false.
 */
export function skipGoogleSlides(
  win: Window | undefined = globalThis.window,
): boolean {
  return !!win && globals(win).DOCBOX_SKIP_GOOGLE_SLIDES === true;
}

/**
 * DOCBOX_EMBED_LINK_POLICY, lower case and trimmed. "mixed_strict" keeps every preview a card that
 * opens on a click (docbox.js:36332). Legacy default: "auto_launch" (docbox.js:46). Read on each
 * check.
 */
export function embedLinkPolicy(win: Window = window): string {
  const value = globals(win).DOCBOX_EMBED_LINK_POLICY;
  return String(value || "")
    .toLowerCase()
    .trim();
}

/**
 * DOCBOX_IFRAME_AUTO_OPEN_INITIAL: true or false, as the host set it, or null when it is neither.
 * false keeps every preview a card (docbox.js:36374). Read on each check.
 */
export function iframeAutoOpenInitial(win: Window = window): boolean | null {
  const value = globals(win).DOCBOX_IFRAME_AUTO_OPEN_INITIAL;
  return typeof value === "boolean" ? value : null;
}

/**
 * The small-card guard, on unless DOCBOX_DISABLE_SMALL_IFRAME_AUTO_OPEN_GUARD === true
 * (docbox.js:36098). Read on each check.
 */
export function smallIframeGuard(win: Window = window): boolean {
  return globals(win).DOCBOX_DISABLE_SMALL_IFRAME_AUTO_OPEN_GUARD !== true;
}

/** The ignore options for link clicks, as shouldIgnoreDocboxPath reads them (docbox.js:35822). */
export type IgnoreOptions = {
  selector: string;
  textRe: RegExp | null;
  urlRe: RegExp | null;
};

/**
 * DOCBOX_IGNORE_LINK_SELECTOR (a string, trimmed), DOCBOX_IGNORE_LINK_TEXT_RE and
 * DOCBOX_IGNORE_URL_RE (a RegExp, or a string compiled case-insensitive). An invalid string is no
 * pattern (docboxBuildRegexMaybe, docbox.js:35791). Legacy reads them on each click.
 */
export function ignoreOptions(win: Window = window): IgnoreOptions {
  const g = globals(win);
  return {
    selector:
      typeof g.DOCBOX_IGNORE_LINK_SELECTOR === "string"
        ? g.DOCBOX_IGNORE_LINK_SELECTOR.trim()
        : "",
    textRe: regexOf(g.DOCBOX_IGNORE_LINK_TEXT_RE),
    urlRe: regexOf(g.DOCBOX_IGNORE_URL_RE),
  };
}

function regexOf(value: unknown): RegExp | null {
  if (!value) return null;
  if (value instanceof RegExp) return value;
  if (typeof value !== "string") return null;
  try {
    return new RegExp(value, "i");
  } catch {
    return null;
  }
}

/**
 * How long a parsed transcript stays in the client cache. DOCBOX_PARSE_CACHE_TTL_MS when it is a
 * positive number, as Number() reads it. Legacy default: 2 hours (docbox.js:5207-5210). Legacy
 * reads it once, when the script loads. A host sets it before then, so a read on use gives the
 * same value. Without a window (the unit tests), the default.
 */
export function parseCacheTtlMs(
  win: Window | undefined = globalThis.window,
): number {
  const value = win && globals(win).DOCBOX_PARSE_CACHE_TTL_MS;
  const ms = value == null ? NaN : Number(value);
  return ms > 0 ? ms : 2 * 60 * 60 * 1000;
}

/**
 * How long Transcript can show its loading screen before the viewer falls back to Original.
 * DOCBOX_ACCESSIBLE_WATCHDOG_MS when Number() reads it as finite, else the parse time limit, kept
 * between 15 seconds and 10 minutes (docbox.js:33289-33302). Legacy reads it on each open.
 */
export function accessibleWatchdogMs(
  win: Window | undefined = globalThis.window,
): number {
  const value = Number(win && globals(win).DOCBOX_ACCESSIBLE_WATCHDOG_MS);
  const ms = Number.isFinite(value) ? value : parseTimeoutMs(win);
  return Math.max(15_000, Math.min(600_000, ms));
}

/**
 * How long the drawer slides. DOCBOX_DRAWER_ANIMATION_MS as parseInt reads it, kept between 280
 * and 900 ms. Legacy default: 580 ms (getDrawerAnimationMs, docbox.js:4886-4891). Legacy reads it
 * on each open.
 */
export function drawerAnimationMs(win: Window = window): number {
  const ms = parseInt(String(globals(win).DOCBOX_DRAWER_ANIMATION_MS), 10);
  if (!Number.isFinite(ms)) return 580;
  return Math.min(900, Math.max(280, ms));
}

/**
 * DOCBOX_INCLUDE_SAME_ORIGIN_IFRAMES === false: document links inside same-origin iframes stay
 * with the iframe (docbox.js:35997, :36863). Legacy reads it at boot and on each iframe scan.
 */
export function includeSameOriginIframes(win: Window = window): boolean {
  return globals(win).DOCBOX_INCLUDE_SAME_ORIGIN_IFRAMES !== false;
}

/**
 * DOCBOX_INTERCEPT_SPA_NAV === false: a history navigation does not scan for new iframes
 * (installSpaNavigationHooks, docbox.js:36038-36040). Legacy reads it at boot.
 */
export function interceptSpaNav(win: Window = window): boolean {
  return globals(win).DOCBOX_INTERCEPT_SPA_NAV !== false;
}

/**
 * DOCBOX_INTERCEPT_FORMS === false: a form that submits to a PDF stays with the host
 * (installFormSubmitInterceptor, docbox.js:36677-36679). Legacy reads it at boot.
 */
export function interceptForms(win: Window = window): boolean {
  return globals(win).DOCBOX_INTERCEPT_FORMS !== false;
}
